import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
type Props = {
  className?: string;
};

/*
 * Flat 2D line composition on a 140-unit module grid — always feels hand-drawn.
 *
 * 1. Intro — on first view the main structure draws itself, starting slowly, like a pen.
 * 2. Golden ratio — inside the golden rectangle, squares and quarter arcs keep drawing,
 *    each one φ times smaller than the last, spiralling inward. No zoom, no rotation.
 * 3. Ongoing — new, very faint construction lines, circles and arcs keep being drawn
 *    across the whole field, snapped to the grid. The oldest quietly fade away, so the
 *    drawing never stops and never clutters.
 */

const PHI = (1 + Math.sqrt(5)) / 2;
const UNIT = 280; // golden rectangle height (width = UNIT·φ)
const PEN = [0.45, 0, 0.15, 1] as const; // slow start, long sure finish
const SLOW = 1.35; // stretches the opening sequence
const GOLDEN_LEVELS = 12;
const GOLDEN_START = 7 * SLOW;
const SPAWN_EVERY = 1.7; // seconds between new background strokes
const MAX_STROKES = 70;
type Pt = [number, number];
// Golden map: R(z)/φ + (φ, 0), R = 90° clockwise — sends the rectangle into its remainder
const f = ([x, y]: Pt): Pt => [-y / PHI + PHI, x / PHI];
const fk = (p: Pt, k: number): Pt => {
  let q = p;
  for (let i = 0; i < k; i++) q = f(q);
  return q;
};
function goldenLevel(k: number) {
  const a = fk([0, 1], k);
  const b = fk([1, 0], k);
  const c = fk([1, 1], k);
  const r = Math.pow(PHI, -k);
  return `M${a[0]} ${a[1]}A${r} ${r} 0 0 1 ${b[0]} ${b[1]}L${c[0]} ${c[1]}`;
}
type Shape = {
  d: string;
  delay: number;
  duration: number;
  tone?: keyof typeof TONE;
  dash?: boolean;
};
const TONE = {
  strong: 'text-parchment-ink/50',
  soft: 'text-parchment-ink/30',
  base: 'text-parchment-ink/[0.24]',
  faint: 'text-parchment-ink/[0.16]',
  ghost: 'text-parchment-ink/[0.11]'
};
const circle = (cx: number, cy: number, r: number) => `M${cx} ${cy - r}A${r} ${r} 0 1 1 ${cx} ${cy + r}A${r} ${r} 0 1 1 ${cx} ${cy - r}`;
const RAW_INTRO: Shape[] = [{
  d: 'M0 280H700',
  delay: 0,
  duration: 2.6
}, {
  d: 'M280 0V280',
  delay: 0.5,
  duration: 1.9
}, {
  d: 'M560 0V560',
  delay: 0.8,
  duration: 2.3
}, {
  d: `M${UNIT * PHI} 280V560`,
  delay: 1.1,
  duration: 1.6
}, {
  d: 'M280 140H560',
  delay: 1.3,
  duration: 1.6
}, {
  d: 'M560 140H700',
  delay: 1.5,
  duration: 1.2
}, {
  d: 'M560 420H700',
  delay: 1.6,
  duration: 1.2
}, {
  d: 'M0 140H280',
  delay: 1.7,
  duration: 1.8,
  tone: 'faint',
  dash: true
}, {
  d: 'M420 140V280',
  delay: 1.8,
  duration: 1.2,
  tone: 'faint',
  dash: true
}, {
  d: 'M630 0V560',
  delay: 1.9,
  duration: 2.4,
  tone: 'faint',
  dash: true
}, {
  d: circle(140, 140, 140),
  delay: 2.0,
  duration: 3.6,
  tone: 'strong'
}, {
  d: 'M140 0V280',
  delay: 2.8,
  duration: 2
}, {
  d: circle(140, 140, 140 / PHI),
  delay: 3.4,
  duration: 2.8,
  tone: 'soft'
}, {
  d: circle(420, 70, 70),
  delay: 2.6,
  duration: 2.8
}, {
  d: circle(420, 70, 70 / PHI),
  delay: 3.8,
  duration: 2.2,
  tone: 'faint'
}, {
  d: 'M560 280A140 140 0 0 1 700 140',
  delay: 3.2,
  duration: 2
}, {
  d: 'M700 420A140 140 0 0 0 560 560',
  delay: 3.6,
  duration: 2,
  tone: 'soft'
}, {
  d: 'M280 280A280 280 0 0 1 560 0',
  delay: 4.0,
  duration: 3,
  tone: 'soft'
}, {
  d: `M0 560L${UNIT * PHI} 280`,
  delay: 4.6,
  duration: 2.6,
  tone: 'faint',
  dash: true
}];
const INTRO = RAW_INTRO.map(s => ({
  ...s,
  delay: s.delay * SLOW,
  duration: s.duration * SLOW
}));
const TICKS = Array.from({
  length: 21
}, (_, i) => i * 35);

/* ---------- ongoing construction strokes ---------- */

type Stroke = {
  id: number;
  d: string;
  tone: keyof typeof TONE;
  dash: boolean;
  duration: number;
  leaving?: boolean;
};
const RADII = [35 / PHI, 35, 70 / PHI, 52.5, 70, 140 / PHI, 105];
const rnd = (n: number) => Math.floor(Math.random() * n);
const pick = <T,>(a: T[]) => a[rnd(a.length)];
const snap = (max: number) => rnd(Math.floor(max / 35) + 1) * 35;
function makeStroke(id: number): Stroke {
  const kind = Math.random();
  const tone: Stroke['tone'] = Math.random() < 0.35 ? 'faint' : 'ghost';
  let d: string;
  let dash = false;
  if (kind < 0.28) {
    // circle on a grid point
    d = circle(snap(700), snap(560), pick(RADII));
  } else if (kind < 0.52) {
    // quarter arc, any quadrant
    const cx = snap(700);
    const cy = snap(560);
    const r = pick(RADII);
    const q = rnd(4);
    const [sx, sy, ex, ey] = [[cx + r, cy, cx, cy + r], [cx, cy + r, cx - r, cy], [cx - r, cy, cx, cy - r], [cx, cy - r, cx + r, cy]][q];
    d = `M${sx} ${sy}A${r} ${r} 0 0 1 ${ex} ${ey}`;
  } else if (kind < 0.8) {
    // horizontal or vertical construction line
    const len = (2 + rnd(7)) * 35;
    if (Math.random() < 0.5) {
      const y = snap(560);
      const x = snap(700 - len);
      d = Math.random() < 0.5 ? `M${x} ${y}H${x + len}` : `M${x + len} ${y}H${x}`;
    } else {
      const x = snap(700);
      const y = snap(560 - len);
      d = Math.random() < 0.5 ? `M${x} ${y}V${y + len}` : `M${x} ${y + len}V${y}`;
    }
    dash = Math.random() < 0.4;
  } else {
    // dotted diagonal between grid points
    const x = snap(560);
    const y = snap(420);
    const dx = (2 + rnd(3)) * 35 * (Math.random() < 0.5 ? 1 : PHI);
    const dy = (2 + rnd(3)) * 35;
    d = `M${x} ${y}L${Math.min(700, x + dx)} ${Math.min(560, y + dy)}`;
    dash = true;
  }
  return {
    id,
    d,
    tone,
    dash,
    duration: 2.4 + Math.random() * 1.8
  };
}
export function PhilosophyGeometry({
  className = ''
}: Props) {
  const reduce = useReducedMotion();
  const svgRef = useRef<SVGSVGElement>(null);
  const started = useInView(svgRef, {
    amount: 0.35,
    once: true
  });
  const inView = useInView(svgRef, {
    amount: 0.1
  });
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const nextId = useRef(0);
  const introDone = useRef(false);

  // Keep drawing new faint strokes while the drawing is on screen
  useEffect(() => {
    if (reduce || !started || !inView) return;
    let timer = 0;
    const spawn = () => {
      setStrokes(list => {
        const next = [...list, makeStroke(nextId.current++)];
        const alive = next.filter(s => !s.leaving);
        if (alive.length > MAX_STROKES) {
          const oldest = alive[0].id;
          return next.map(s => s.id === oldest ? {
            ...s,
            leaving: true
          } : s);
        }
        return next;
      });
      timer = window.setTimeout(spawn, SPAWN_EVERY * 1000);
    };
    // first run waits for the opening sequence
    const wait = introDone.current ? SPAWN_EVERY : GOLDEN_START + 2;
    introDone.current = true;
    timer = window.setTimeout(spawn, wait * 1000);
    return () => window.clearTimeout(timer);
  }, [reduce, started, inView]);
  const removeStroke = (id: number) => setStrokes(list => list.filter(s => s.id !== id));
  const line = (s: Shape) => <motion.path key={s.d} d={s.d} fill="none" stroke="currentColor" strokeWidth={1} strokeDasharray={s.dash ? '2 5' : undefined} vectorEffect="non-scaling-stroke" className={TONE[s.tone ?? 'base']} variants={{
    hidden: {
      pathLength: 0,
      opacity: 0
    },
    shown: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          delay: s.delay,
          duration: s.duration,
          ease: PEN
        },
        opacity: {
          delay: s.delay,
          duration: 0.3
        }
      }
    }
  }} data-id="element-336" data-mp="7v3iua7y9ool" />;
  return <motion.svg ref={svgRef} viewBox="0 0 700 560" preserveAspectRatio="xMidYMid slice" className={`block ${className}`} aria-hidden="true" initial={reduce ? 'shown' : 'hidden'} animate={started || reduce ? 'shown' : 'hidden'} data-id="element-337" data-mp="48x67t69ic4r">
      {/* Ongoing construction layer — sits behind everything */}
      <g fill="none" stroke="currentColor" data-id="element-338" data-mp="3mgcwdzxa9emg">
        {strokes.map(s => <g key={s.id} className={TONE[s.tone]} data-id="element-339" data-mp="1on7722e1m200">
            {/* "dash" strokes draw as softer guide lines */}
            <path d={s.d} pathLength={1} vectorEffect="non-scaling-stroke" className={s.leaving ? 'gen-leave' : 'golden-draw'} style={{
          animationDuration: s.leaving ? undefined : `${s.duration}s`
        }} onAnimationEnd={s.leaving ? () => removeStroke(s.id) : undefined} opacity={s.dash ? 0.7 : 1} data-id="element-340" data-mp="1knoovedlw50v" />
          </g>)}
      </g>

      {INTRO.map(line)}

      {TICKS.map((x, i) => <motion.path key={x} d={`M${x} 280V${i % 4 === 0 ? 290 : 285}`} stroke="currentColor" vectorEffect="non-scaling-stroke" className={TONE.soft} variants={{
      hidden: {
        opacity: 0
      },
      shown: {
        opacity: 1,
        transition: {
          delay: (2.4 + i * 0.06) * SLOW,
          duration: 0.3
        }
      }
    }} data-id="element-341" data-mp="1k64hm3xn7qma" data-mpi={i} />)}

      {/* Golden rectangle — squares and arcs drawing inward, ever smaller */}
      <g transform={`translate(0 280) scale(${UNIT})`} data-id="element-342" data-mp="3akdeww27v7qi">
        <motion.rect x={0} y={0} width={PHI} height={1} fill="none" stroke="currentColor" strokeWidth={1 / UNIT} className={TONE.strong} variants={{
        hidden: {
          opacity: 0
        },
        shown: {
          opacity: 1,
          transition: {
            delay: 5 * SLOW,
            duration: 0.9
          }
        }
      }} data-id="element-343" data-mp="3ahr7cwfbss8a" />
        <g fill="none" stroke="currentColor" strokeWidth={1 / UNIT} className={TONE.strong} data-id="element-344" data-mp="3ooce4grvcrv1">
          {Array.from({
          length: GOLDEN_LEVELS
        }, (_, k) => <path key={k} d={goldenLevel(k)} pathLength={1} className={reduce ? undefined : started ? 'golden-draw' : 'opacity-0'} style={{
          animationDelay: `${GOLDEN_START + k * 2.4 * Math.pow(0.94, k)}s`,
          animationDuration: `${Math.max(1.2, 3 - k * 0.15)}s`
        }} data-id="element-345" data-mp="2aub6pnto16t" />)}
        </g>
      </g>

      <motion.rect x={277} y={277} width={6} height={6} className="fill-parchment-ink" variants={{
      hidden: {
        opacity: 0,
        scale: 0.96
      },
      shown: {
        opacity: 1,
        scale: 1,
        transition: {
          delay: 5.4 * SLOW,
          duration: 0.3
        }
      }
    }} data-id="element-346" data-mp="2e0wu2wekf9du" />
    </motion.svg>;
}